<template>
  <div id="app">
    <!-- Navigation Bar -->
    <nav class="navbar" :class="{ 'scrolled': isScrolled }">
      <div class="nav-container">
        <div class="nav-brand">
          <img src="@/assets/TrumpetLogo-sm.png" alt="Matsu Hope Radio Logo" class="brand-logo">
          <div class="brand-frequency">97.7</div>
          <div class="brand-text">
            <div class="brand-name">MATSU HOPE</div>
            <div class="brand-subtitle">RADIO</div>
          </div>
        </div>
        <button class="mobile-toggle" @click="toggleMobileMenu" :class="{ 'active': mobileMenuOpen }">
          <span></span>
          <span></span>
          <span></span>
        </button>
        <ul class="nav-menu" :class="{ 'active': mobileMenuOpen }">
          <li><a href="#home" @click="closeMobileMenu">Home</a></li>
          <li v-if="hasDevotional"><a href="#devotional" @click="closeMobileMenu">Devotional</a></li>
          <li><a href="#about" @click="closeMobileMenu">About</a></li>
          <li><a href="#programs" @click="closeMobileMenu">Programs</a></li>
          <!-- <li><a href="#weather" @click="closeMobileMenu">Weather</a></li> -->
          <li v-if="questionsEnabled"><a href="#questions" @click="closeMobileMenu">Questions</a></li>
          <li><a href="#contact" @click="closeMobileMenu">Contact</a></li>
          <!-- <li><a href="#listen" class="listen-btn" @click="closeMobileMenu">
            <span class="pulse-dot"></span> Listen Live
          </a></li> -->
        </ul>
      </div>
    </nav>

    <!-- Hero Section -->
    <section id="home" class="hero-section">
      <div class="hero-banner">
        <img src="@/assets/Banner-page.png" alt="Matsu Hope Radio 97.7 FM" class="banner-image">
      </div>
      <div class="hero-content">
        <p class="hero-description">
          Bringing faith, hope, and inspiration to the Matanuska-Susitna Valley through 
          uplifting Christian music and meaningful programming
        </p>
        <div class="hero-actions">
          <button class="hero-btn primary-btn" @click="scrollToSection('programs')">
            View Programs
          </button>
          <button class="hero-btn secondary-btn" @click="scrollToSection('contact')">
            Get In Touch
          </button>
        </div>
        <div class="hero-stats">
          <div class="stat">
            <div class="stat-icon">🎵</div>
            <div class="stat-text">Christian Music</div>
          </div>
          <div class="stat">
            <div class="stat-icon">📖</div>
            <div class="stat-text">Bible-Based Teaching</div>
          </div>
          <div class="stat">
            <div class="stat-icon">❤️</div>
            <div class="stat-text">Community Focused</div>
          </div>
        </div>
      </div>
      <div class="scroll-indicator">
        <div class="mouse"></div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="about-section">
      <div class="section-container">
        <div class="section-header">
          <span class="section-tag">WHO WE ARE</span>
          <h2 class="section-title">About Matsu Hope Radio</h2>
          <div class="title-underline"></div>
        </div>
        <div class="about-grid">
          <div class="about-image">
            <div class="image-placeholder">
              <div class="radio-waves">
                <div class="wave"></div>
                <div class="wave"></div>
                <div class="wave"></div>
              </div>
              <div class="mic-icon">🎙️</div>
            </div>
          </div>
          <div class="about-text">
            <h3 class="about-heading">Spreading Hope Across Alaska</h3>
            <p class="about-paragraph">
              Matsu Hope Radio 97.7 FM is dedicated to bringing uplifting Christian programming 
              to the Matanuska-Susitna Valley. We believe in the power of faith-based media to 
              transform lives and strengthen communities.
            </p>
            <p class="about-paragraph">
              Our mission is to spread hope, faith, and encouragement through quality Christian 
              music, inspiring biblical teaching, and community-focused content that reflects 
              the values and needs of the Mat-Su Valley.
            </p>
            <div class="about-values">
              <div class="value-item">
                <div class="value-icon">🎯</div>
                <div class="value-content">
                  <h4>Our Mission</h4>
                  <p>Broadcasting Good News that transforms hearts and minds</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Devotional Section -->
    <section v-if="hasDevotional" id="devotional" class="devotional-section">
      <div class="section-container">
        <div class="section-header center">
          <span class="section-tag">INSPIRATION</span>
          <h2 class="section-title">Devotionals</h2>
          <div class="title-underline center"></div>
        </div>
        <DevotionalCard @devotional-loaded="onDevotionalLoaded" @devotional-error="onDevotionalError" />
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="section-container">
        <div class="features-grid">
          <div class="feature-box">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <span class="feature-emoji">🎵</span>
            </div>
            <h3 class="feature-title">Christian Music</h3>
            <p class="feature-description">
              Christian music that is meant to help you grow in your faith and find comfort in God's love.
            </p>
          </div>
          <div class="feature-box">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <span class="feature-emoji">📖</span>
            </div>
            <h3 class="feature-title">Biblical Teaching</h3>
            <p class="feature-description">
              In-depth Bible studies and teachings from respected Christian leaders 
              and theologians
            </p>
          </div>
          <div class="feature-box">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <span class="feature-emoji">🌟</span>
            </div>
            <h3 class="feature-title">Inspirational Content</h3>
            <p class="feature-description">
              Encouraging messages and testimonies that strengthen faith and 
              inspire daily living
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Calendar Section -->
    <section v-if="hasCalendarItems" id="calendar" class="events-section">
      <div class="section-container">
        <div class="section-header center">
          <span class="section-tag">UPCOMING</span>
          <h2 class="section-title">Calendar</h2>
          <div class="title-underline center"></div>
          <p class="section-description">
            A simple calendar of special programs, station highlights, and community events.
          </p>
        </div>

        <div v-if="eventsLoading" class="events-state">Loading calendar items…</div>
        <div v-else-if="events.length" class="events-list">
          <article v-for="event in events" :key="event.id || event.title" class="event-card">
            <div class="event-card__meta">
              <span v-if="event.date" class="event-date">{{ formatEventDate(event.date) }}</span>
              <span v-if="event.location" class="event-location">{{ event.location }}</span>
              <span v-if="event.type" class="event-type">{{ event.type }}</span>
            </div>
            <h3 class="event-card__title" v-html="formatCalendarText(event.title)"></h3>
            <p v-if="event.description" class="event-card__description" v-html="formatCalendarText(event.description)"></p>
          </article>
        </div>
        <div v-else class="events-state">
          There are no calendar items to display right now.
        </div>
      </div>
    </section>

    <!-- Programs Section -->
    <section id="programs" class="programs-section">
      <div class="section-container">
        <div class="section-header center">
          <span class="section-tag">WHAT'S ON AIR</span>
          <h2 class="section-title">Our Programs</h2>
          <div class="title-underline center"></div>
        </div>
        
        <div class="programs-intro">
          <p class="programs-description">
            Matsu Hope Radio 97.7 FM features programming from the Alaska Adventist Radio Network, 
            bringing you uplifting Christian music, biblical teaching, and family-friendly content 24/7.
          </p>
        </div>

        <div class="program-highlights">
          <div class="program-card">
            <div class="program-icon">📖</div>
            <h3>It Is Written</h3>
            <p>Timeless truths applied to everyday life with John Bradshaw</p>
          </div>
          <div class="program-card">
            <div class="program-icon">❤️</div>
            <h3>Heartwise</h3>
            <p>Practical wisdom for healthy relationships and family life</p>
          </div>
          <div class="program-card">
            <div class="program-icon">🎙️</div>
            <h3>Authentic</h3>
            <p>Real conversations about faith and life</p>
          </div>
          <div class="program-card">
            <div class="program-icon">�</div>
            <h3>Bible Answers Live</h3>
            <p>Getting answers to your Bible questions</p>
          </div>
          <div class="program-card">
            <div class="program-icon">👨‍👩‍👧‍👦</div>
            <h3>Your Story Hour</h3>
            <p>Inspiring stories for the whole family</p>
          </div>
          <div class="program-card">
            <div class="program-icon">🪶</div>
            <h3>American Indian Living</h3>
            <p>Celebrating Native American culture and faith</p>
          </div>
          <div class="program-card">
            <div class="program-icon">✨</div>
            <h3>And Much More!</h3>
            <p>Teaching, worship, and inspirational content throughout the day</p>
          </div>
        </div>

        <div class="schedule-cta">
          <h3>View Full Schedule</h3>
          <p>Check out the complete daily program schedule for all Alaska Adventist Radio Network stations</p>
          <a href="https://akadventistradio.net/cms/programs/" 
             class="schedule-btn" 
             target="_blank" 
             rel="noopener noreferrer">
            <span>View Complete Schedule</span>
            <svg class="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M5 12h14M12 5l7 7-7 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
          <p class="network-note">
            Programming provided by <a href="https://akadventistradio.net/cms" target="_blank" rel="noopener noreferrer">Alaska Adventist Radio Network</a>
          </p>
        </div>
      </div>
    </section>

    <!-- Weather Section - Temporarily Disabled -->
    <!-- <section id="weather" class="weather-section">
      <div class="section-container">
        <div class="section-header center">
          <span class="section-tag">LOCAL FORECAST</span>
          <h2 class="section-title">Today's Weather Updates</h2>
          <div class="title-underline center"></div>
          <p class="section-description">
            Listen to the latest weather forecasts for the Matanuska-Susitna Valley
          </p>
        </div>
        <WeatherAudio />
      </div>
    </section> -->

    <!-- Question Submission Section -->
    <section v-if="questionsEnabled" id="questions" class="questions-section">
      <div class="section-container">
        <div class="section-header center">
          <span class="section-tag">ASK US</span>
          <h2 class="section-title">Submit Your Question</h2>
          <div class="title-underline center"></div>
          <p class="section-description">
            Have a biblical question? Send it to us and we'll help you find the answer.
          </p>
        </div>
        <QuestionSubmission />
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="contact-section">
      <div class="section-container">
        <div class="contact-grid">
          <div class="contact-info-side">
            <div class="section-header">
              <span class="section-tag light">GET IN TOUCH</span>
              <h2 class="section-title light">Contact Us</h2>
              <div class="title-underline"></div>
            </div>
            <p class="contact-intro">
              We'd love to hear from you! Whether you have questions, prayer requests, 
              or want to learn more about Matsu Hope Radio, feel free to reach out.
            </p>
            <div class="contact-methods">
              <div class="contact-method">
                <div class="method-icon">📧</div>
                <div class="method-details">
                  <h4>Email Us</h4>
                  <a href="mailto:info@matsuhope.org">info@matsuhope.org</a>
                </div>
              </div>
              <div class="contact-method">
                <div class="method-icon">📻</div>
                <div class="method-details">
                  <h4>Frequency</h4>
                  <p >97.7 FM • Wasilla, Alaska</p>
                </div>
              </div>
              <div class="contact-method">
                <div class="method-icon">⏰</div>
                <div class="method-details">
                  <h4>Broadcasting</h4>
                  <p>24 hours a day, 7 days a week</p>
                </div>
              </div>
              <div class="contact-method">
                <div class="method-icon">📱</div>
                <div class="method-details">
                  <h4>Get SMS Updates</h4>
                  <a href="/sms-signup.html">Subscribe to text messages</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-top">
          <div class="footer-brand">
            <div class="footer-logo">
              <div class="footer-frequency">97.7</div>
              <div class="footer-name">MATSU HOPE RADIO</div>
            </div>
            <p class="footer-tagline">Broadcasting Good News to the Mat-Su Valley</p>
          </div>
          <div class="footer-links">
            <div class="footer-column">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#devotional">Devotional</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#programs">Programs</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            <div class="footer-column">
              <h4>Connect</h4>
              <ul>
                <li><a href="mailto:info@matsuhope.org">Email Us</a></li>
                <!-- <li><a href="#listen">Listen Live</a></li> -->
              </ul>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; {{ currentYear }} Matsu Hope Radio. All rights reserved.</p>
          <p class="footer-location">Wasilla, Alaska • 97.7 FM</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import DevotionalCard from './components/DevotionalCard.vue';
import QuestionSubmission from '@/components/QuestionSubmission.vue';

export default {
  name: 'App',
  components: {
    DevotionalCard,
    QuestionSubmission
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      isScrolled: false,
      mobileMenuOpen: false,
      hasDevotional: true,
      questionsEnabled: false,
      events: [],
      eventsLoading: true
    }
  },
  computed: {
    hasCalendarItems() {
      return this.events.length > 0;
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    this.checkQuestionsEnabled();
    this.loadEvents();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      this.closeMobileMenu();
    },
    playStream() {
      alert('Live streaming will be available once we\'re on air! Stay tuned to 97.7 FM.');
    },
    onDevotionalLoaded() {
      this.hasDevotional = true;
    },
    onDevotionalError() {
      this.hasDevotional = false;
    },
    async checkQuestionsEnabled() {
      try {
        const response = await fetch('/api/question-status');
        if (response.ok) {
          const result = await response.json();
          // Enable questions section if API is available and functioning
          this.questionsEnabled = result.total && result.total.canSubmit !== undefined;
        }
      } catch (err) {
        this.questionsEnabled = false;
      }
    },
    async loadEvents() {
      this.eventsLoading = true;
      const fallbackEvents = [];

      try {
        const response = await fetch('/api/events');
        if (!response.ok) {
          throw new Error('Unable to load events');
        }
        const result = await response.json();
        const loadedEvents = Array.isArray(result?.data?.events) ? result.data.events : [];
        this.events = loadedEvents.length ? loadedEvents : fallbackEvents;
      } catch (err) {
        console.error('Failed to load events', err);
        this.events = fallbackEvents;
      } finally {
        this.eventsLoading = false;
      }
    },
    formatCalendarText(text) {
      if (!text) {
        return '';
      }

      const escaped = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');

      return escaped
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*]+)\*([^*]|$)/g, '$1<em>$2</em>$3');
    },
    formatEventDate(dateValue) {
      if (!dateValue) {
        return '';
      }

      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return dateValue;
      }

      return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    }
  }
}
</script>

<style>
/* ============================================
   CSS RESET & BASE STYLES
   ============================================ */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  /* Brand Colors - Coordinated with Banner */
  --primary-color: #1e88e5;
  --primary-dark: #1565c0;
  --primary-light: #42a5f5;
  --secondary-color: #00acc1;
  --accent-color: #26c6da;
  --gold-accent: #ffa726;
  
  /* Neutral Colors */
  --white: #ffffff;
  --gray-50: #f8f9fa;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --gray-800: #1f2937;
  --gray-900: #111827;
  
  /* Spacing */
  --container-padding: 20px;
  --section-padding: 80px 0;
  
  /* Transitions */
  --transition-fast: 0.2s ease;
  --transition-normal: 0.3s ease;
  --transition-slow: 0.5s ease;
  
  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px rgba(0, 0, 0, 0.15);
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  color: var(--gray-800);
  background: var(--white);
  overflow-x: hidden;
}

#app {
  min-height: 100vh;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--container-padding);
}

/* ============================================
   SECTION HEADERS
   ============================================ */
.section-header {
  text-align: left;
  margin-bottom: 60px;
}

.section-header.center {
  text-align: center;
}

.section-tag {
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--primary-color);
  margin-bottom: 15px;
}

.section-tag.light {
  color: var(--gold-accent);
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--gray-900);
  margin-bottom: 15px;
  line-height: 1.2;
}

.section-title.light {
  color: var(--white);
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  border-radius: 2px;
}

.title-underline.center {
  margin: 0 auto;
}

.section-subtitle {
  font-size: 1.125rem;
  color: var(--gray-600);
  max-width: 700px;
  margin-top: 20px;
}

/* ============================================
   NAVIGATION BAR
   ============================================ */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-normal);
}

.navbar.scrolled {
  box-shadow: var(--shadow-md);
  background: rgba(255, 255, 255, 0.98);
}

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.brand-logo {
  height: 50px;
  width: auto;
  object-fit: contain;
}

.brand-frequency {
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--primary-color);
  line-height: 1;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.brand-name {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--gray-900);
  letter-spacing: 1px;
}

.brand-subtitle {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-600);
  letter-spacing: 2px;
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
}

.mobile-toggle span {
  width: 25px;
  height: 3px;
  background: var(--gray-800);
  border-radius: 2px;
  transition: all var(--transition-normal);
}

.mobile-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translate(8px, 8px);
}

.mobile-toggle.active span:nth-child(2) {
  opacity: 0;
}

.mobile-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -7px);
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 5px;
  align-items: center;
}

.nav-menu li a {
  display: block;
  padding: 10px 20px;
  color: var(--gray-700);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 8px;
  transition: all var(--transition-normal);
  position: relative;
}

.nav-menu li a:hover {
  color: var(--primary-color);
  background: var(--gray-50);
}

.nav-menu li a.listen-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  color: var(--white);
  padding: 10px 25px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
}

.nav-menu li a.listen-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: var(--white);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

/* ============================================
   HERO SECTION
   ============================================ */
.hero-section {
  position: relative;
  min-height: 85vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 100px 20px 60px;
  background: linear-gradient(180deg, #e3f2fd 0%, #ffffff 100%);
}

.hero-banner {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto 40px;
  animation: fadeInDown 1s ease;
}

.banner-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(30, 136, 229, 0.2);
}

.hero-content {
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  color: var(--gray-800);
}

.hero-description {
  font-size: 1.25rem;
  line-height: 1.8;
  color: var(--gray-700);
  max-width: 700px;
  margin: 0 auto 40px;
  animation: fadeInUp 1s ease 0.2s backwards;
}

.hero-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 50px;
  animation: fadeInUp 1s ease 0.4s backwards;
}

.hero-btn {
  padding: 16px 40px;
  font-size: 1.125rem;
  font-weight: 700;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  gap: 10px;
}

.primary-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: var(--white);
  box-shadow: 0 4px 15px rgba(30, 136, 229, 0.3);
}

.primary-btn:hover {
  background: linear-gradient(135deg, var(--primary-dark), var(--secondary-color));
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(30, 136, 229, 0.4);
}

.secondary-btn {
  background: var(--white);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
}

.secondary-btn:hover {
  background: var(--primary-color);
  color: var(--white);
  transform: translateY(-3px);
  box-shadow: 0 4px 15px rgba(30, 136, 229, 0.3);
}

.play-icon {
  width: 20px;
  height: 20px;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  animation: fadeInUp 1s ease 0.6s backwards;
}

.stat {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--white);
  padding: 15px 25px;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(30, 136, 229, 0.1);
  border: 1px solid var(--gray-200);
}

.stat-icon {
  font-size: 2rem;
}

.stat-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--gray-700);
}

.scroll-indicator {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  animation: fadeIn 1s ease 1s backwards;
}

.mouse {
  width: 28px;
  height: 45px;
  border: 2px solid var(--primary-color);
  border-radius: 20px;
  position: relative;
}

.mouse::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 10px;
  background: var(--primary-color);
  border-radius: 2px;
  animation: scroll 2s infinite;
}

@keyframes scroll {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(15px);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ============================================
   ABOUT SECTION
   ============================================ */
.about-section {
  padding: var(--section-padding);
  background: var(--white);
}

.about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.about-image {
  position: relative;
}

.image-placeholder {
  background: linear-gradient(135deg, var(--primary-light) 0%, var(--accent-color) 100%);
  border-radius: 20px;
  padding: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-height: 400px;
}

.radio-waves {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.wave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: wave 3s infinite ease-out;
}

.wave:nth-child(2) {
  animation-delay: 1s;
}

.wave:nth-child(3) {
  animation-delay: 2s;
}

@keyframes wave {
  0% {
    width: 100px;
    height: 100px;
    opacity: 1;
  }
  100% {
    width: 300px;
    height: 300px;
    opacity: 0;
  }
}

.mic-icon {
  font-size: 5rem;
  position: relative;
  z-index: 10;
}

.about-text {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.about-heading {
  font-size: 2rem;
  font-weight: 700;
  color: var(--gray-900);
  line-height: 1.3;
}

.about-paragraph {
  font-size: 1.0625rem;
  line-height: 1.8;
  color: var(--gray-600);
}

.about-values {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 15px;
}

.value-item {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.value-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.value-content h4 {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 5px;
}

.value-content p {
  font-size: 0.9375rem;
  color: var(--gray-600);
  line-height: 1.6;
}

/* ============================================
   DEVOTIONAL SECTION
   ============================================ */
.devotional-section {
  padding: var(--section-padding);
  background: linear-gradient(180deg, var(--white) 0%, var(--gray-50) 100%);
}

/* ============================================
   FEATURES SECTION
   ============================================ */
.features-section {
  padding: var(--section-padding);
  background: var(--gray-50);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
}

.feature-box {
  background: var(--white);
  padding: 40px 30px;
  border-radius: 15px;
  text-align: center;
  transition: all var(--transition-normal);
  border: 2px solid transparent;
}

.feature-box:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
  border-color: var(--primary-light);
}

.feature-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 25px;
}

.feature-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--primary-light), var(--accent-color));
  border-radius: 20px;
  opacity: 0.15;
  transition: all var(--transition-normal);
}

.feature-box:hover .feature-icon-bg {
  transform: translate(-50%, -50%) rotate(45deg) scale(1.1);
  opacity: 0.2;
}

.feature-emoji {
  font-size: 3rem;
  position: relative;
  z-index: 10;
  display: block;
}

.feature-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 15px;
}

.feature-description {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--gray-600);
}

/* ============================================
   EVENTS SECTION
   ============================================ */
.events-section {
  padding: var(--section-padding);
  background: linear-gradient(180deg, var(--gray-50) 0%, var(--white) 100%);
}

.events-list {
  display: grid;
  gap: 20px;
}

.event-card {
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: var(--shadow-sm);
}

.event-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
  color: var(--primary-color);
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.event-card__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 10px;
}

.event-card__description {
  color: var(--gray-600);
  line-height: 1.7;
}

.event-card__description strong,
.event-card__title strong {
  color: var(--gray-900);
  font-weight: 700;
}

.event-card__description em,
.event-card__title em {
  font-style: italic;
}

.event-card__description a,
.event-card__title a {
  color: var(--primary-color);
  text-decoration: underline;
}

.event-card__description br,
.event-card__title br {
  display: block;
  content: '';
  margin-top: 0.5rem;
}

.events-state {
  text-align: center;
  padding: 30px;
  border-radius: 16px;
  background: var(--white);
  color: var(--gray-600);
  border: 1px dashed var(--gray-300);
}

/* ============================================
   PROGRAMS SECTION
   ============================================ */
.programs-section {
  padding: var(--section-padding);
  background: var(--white);
}

.programs-intro {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 50px;
}

.programs-description {
  font-size: 1.125rem;
  line-height: 1.8;
  color: var(--gray-600);
}

.program-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 60px;
}

.program-card {
  background: var(--gray-50);
  padding: 30px 25px;
  border-radius: 15px;
  text-align: center;
  transition: all var(--transition-normal);
  border: 2px solid transparent;
}

.program-card:hover {
  background: var(--white);
  border-color: var(--primary-color);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.program-icon {
  font-size: 2.5rem;
  margin-bottom: 15px;
  display: block;
}

.program-card h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 10px;
}

.program-card p {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--gray-600);
  margin: 0;
}

.schedule-cta {
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--white) 100%);
  padding: 50px 40px;
  border-radius: 20px;
  text-align: center;
  border: 2px solid var(--gray-200);
}

.schedule-cta h3 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 15px;
}

.schedule-cta > p {
  font-size: 1.0625rem;
  line-height: 1.7;
  color: var(--gray-600);
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.schedule-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  color: var(--white);
  padding: 16px 40px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.125rem;
  transition: all var(--transition-normal);
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
  margin-bottom: 20px;
}

.schedule-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
}

.arrow-icon {
  width: 20px;
  height: 20px;
  transition: transform var(--transition-normal);
}

.schedule-btn:hover .arrow-icon {
  transform: translateX(5px);
}

.network-note {
  font-size: 0.9375rem;
  color: var(--gray-600);
  margin-top: 20px;
}

.network-note a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: color var(--transition-normal);
}

.network-note a:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

/* ============================================
   FCC SECTION
   ============================================ */
.fcc-section {
  padding: var(--section-padding);
  background: var(--gray-50);
}

.fcc-card {
  max-width: 900px;
  margin: 0 auto;
  background: var(--white);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.fcc-header {
  background: linear-gradient(135deg, var(--gray-800), var(--gray-700));
  color: var(--white);
  padding: 30px 40px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.fcc-icon {
  font-size: 2.5rem;
}

.fcc-header h3 {
  font-size: 1.75rem;
  font-weight: 700;
}

.fcc-content {
  padding: 40px;
}

.fcc-content p {
  font-size: 1.0625rem;
  line-height: 1.8;
  color: var(--gray-600);
  margin-bottom: 20px;
}

.fcc-content strong {
  color: var(--primary-color);
  font-weight: 700;
}

.fcc-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--primary-color);
  color: var(--white);
  padding: 14px 30px;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  transition: all var(--transition-normal);
  margin-top: 10px;
}

.fcc-link:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.link-icon {
  width: 20px;
  height: 20px;
}

/* ============================================
   WEATHER SECTION
   ============================================ */
.weather-section {
  padding: var(--section-padding);
  background: var(--white);
}

/* ============================================
   QUESTIONS SECTION
   ============================================ */
.questions-section {
  padding: 80px 20px;
  background: var(--gray-50);
}

.section-description {
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  margin-top: 1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* ============================================
   CONTACT SECTION
   ============================================ */
.contact-section {
  padding: var(--section-padding);
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%);
  color: var(--white);
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  align-items: flex-start;
}

.contact-info-side {
  text-align: left;
}

.contact-form-side {
  display: flex;
  justify-content: center;
}

.contact-intro {
  font-size: 1.0625rem;
  line-height: 1.8;
  opacity: 0.95;
  margin-bottom: 40px;
}

.contact-methods {
  display: flex;
  flex-direction: row;
  gap: 30px;
  justify-content: flex-start;
}

.contact-method {
  display: flex;
  flex-direction: row;
  gap: 15px;
  align-items: flex-start;
  flex: 0 0 auto;
}

.method-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.method-details {
  text-align: left;
}

.method-details h4 {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.method-details p,
.method-details a {
  font-size: 1rem;
  opacity: 0.9;
  color: var(--white);
  text-decoration: none;
  transition: opacity var(--transition-normal);
}

.method-details a:hover {
  opacity: 1;
  text-decoration: underline;
}

.contact-card {
  background: var(--white);
  padding: 40px;
  border-radius: 20px;
  box-shadow: var(--shadow-xl);
}

.contact-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 25px;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-group input,
.form-group textarea {
  padding: 14px 16px;
  border: 2px solid var(--gray-200);
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  transition: all var(--transition-normal);
  background: var(--gray-50);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  background: var(--white);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
}

.submit-btn {
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  color: var(--white);
  border: none;
  border-radius: 10px;
  font-size: 1.0625rem;
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
}

.send-icon {
  width: 20px;
  height: 20px;
}

/* ============================================
   FOOTER
   ============================================ */
.site-footer {
  background: var(--gray-900);
  color: var(--white);
  padding: 60px 0 30px;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--container-padding);
}

.footer-top {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 30px;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 15px;
}

.footer-frequency {
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--secondary-color);
  line-height: 1;
}

.footer-name {
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 1px;
  line-height: 1.2;
}

.footer-tagline {
  font-size: 1rem;
  opacity: 0.8;
  font-style: italic;
  max-width: 400px;
}

.footer-links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

.footer-column h4 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.footer-column ul {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-column ul li a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color var(--transition-normal);
}

.footer-column ul li a:hover {
  color: var(--white);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  font-size: 0.875rem;
  opacity: 0.7;
}

.footer-location {
  font-weight: 600;
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
@media (max-width: 1024px) {
  .about-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

@media (max-width: 768px) {
  /* Navigation */
  .mobile-toggle {
    display: flex;
    position: relative;
    z-index: 1002;
  }

  .nav-menu {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    flex-direction: column;
    background: var(--white);
    padding: 20px;
    box-shadow: var(--shadow-lg);
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: all var(--transition-normal);
    max-height: calc(100vh - 80px);
    overflow-y: auto;
    z-index: 1001;
  }

  .nav-menu.active {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }

  .nav-menu li {
    width: 100%;
  }

  .nav-menu li a {
    display: block;
    width: 100%;
    text-align: center;
    padding: 15px 20px;
  }

  .nav-menu li a.listen-btn {
    margin-left: 0;
    margin-top: 10px;
    justify-content: center;
  }

  /* Hero */
  .hero-section {
    padding: 100px 20px 50px;
    min-height: auto;
  }

  .hero-banner {
    margin-bottom: 30px;
  }

  .banner-image {
    border-radius: 10px;
  }

  .hero-description {
    font-size: 1rem;
    margin-bottom: 30px;
  }

  .hero-stats {
    gap: 15px;
  }

  .stat {
    flex-direction: column;
    text-align: center;
    padding: 12px 20px;
    font-size: 0.875rem;
  }

  .stat-icon {
    font-size: 1.75rem;
  }

  /* Sections */
  .section-title {
    font-size: 2rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  /* About */
  .image-placeholder {
    min-height: 300px;
    padding: 60px;
  }

  .about-heading {
    font-size: 1.625rem;
  }

  /* Features */
  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  /* Programs */
  .program-highlights {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .schedule-cta {
    padding: 40px 25px;
  }

  /* Questions */
  .questions-section {
    padding: 60px 15px;
  }

  /* Contact */
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  .contact-info-side {
    text-align: center;
  }

  .contact-methods {
    flex-direction: column;
    gap: 25px;
  }

  .contact-card {
    padding: 30px 20px;
  }

  /* Footer */
  .footer-top {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .footer-links {
    grid-template-columns: 1fr 1fr;
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .nav-container {
    height: 70px;
  }

  .brand-logo {
    height: 40px;
  }

  .brand-frequency {
    font-size: 2rem;
  }

  .brand-name {
    font-size: 1rem;
  }

  .brand-subtitle {
    font-size: 0.75rem;
  }

  .hero-frequency {
    font-size: 2.5rem;
  }

  .hero-station {
    font-size: 1.5rem;
  }

  .hero-slogan {
    font-size: 1.125rem;
  }

  .hero-actions {
    flex-direction: column;
    width: 100%;
  }

  .hero-btn {
    width: 100%;
    justify-content: center;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .about-heading {
    font-size: 1.375rem;
  }

  .image-placeholder {
    padding: 40px;
    min-height: 250px;
  }

  .mic-icon {
    font-size: 3rem;
  }

  .footer-links {
    grid-template-columns: 1fr;
    gap: 30px;
  }
}
</style>
